import { useGetCareOrdersQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { EMPTY_ARRAY, useCallbackRef, type CareOrder } from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { SelectableCareOrder, type SelectableCareOrderSelectionState } from './SelectableCareOrder';
import {
  CareOrderNettingPreviewMode,
  type CareOrderNettingPreviewMode as CareOrderNettingPreviewModeType,
} from './useWorkingOrderPreview';

/**
 * Builds the initial selection state map used by care-order netting preview.
 *
 * In modify mode, preselects member care orders from the existing netted order
 * (or falls back to selected care orders when member list is unavailable).
 * In create mode, preselects the passed selected care orders.
 */
const getInitialSelectionStateByID = (
  selectedCareOrders: CareOrder[],
  mode: CareOrderNettingPreviewModeType,
  nettedCareOrder?: CareOrder
) => {
  const selectedCareOrderIDsForMode =
    mode === CareOrderNettingPreviewMode.Modify
      ? (nettedCareOrder?.MemberCareOrders?.map(memberCareOrder => memberCareOrder.OrderID) ??
        selectedCareOrders.map(careOrder => careOrder.OrderID))
      : selectedCareOrders.map(careOrder => careOrder.OrderID);
  const nettedMemberCareOrderIDs = new Set(
    nettedCareOrder?.MemberCareOrders?.map(memberCareOrder => memberCareOrder.OrderID) ?? []
  );

  return new Map<string, SelectableCareOrderSelectionState>(
    selectedCareOrderIDsForMode.map(orderID => [
      orderID,
      {
        selected: true,
        hasBeenSelected: true,
        inNettedOrder: mode === CareOrderNettingPreviewMode.Modify ? nettedMemberCareOrderIDs.has(orderID) : false,
      },
    ])
  );
};

/**
 * Encapsulates selectable care-order state for netting preview.
 *
 * Returns:
 * - `allCareOrders`: streamed care orders enriched with local selection state
 * - `selectedCareOrderIDs`: currently selected care-order IDs
 * - `careOrderSelectionStateByID`: selection state map by OrderID
 * - `onSelectionChanged`: handler for toggling care-order selection
 */
export function useSelectableCareOrders({
  mode,
  selectedCareOrders,
  nettedCareOrder,
}: {
  mode: CareOrderNettingPreviewModeType;
  selectedCareOrders: CareOrder[];
  nettedCareOrder?: CareOrder;
}) {
  const [careOrderSelectionStateByID, setCareOrderSelectionStateByID] = useState<
    Map<string, SelectableCareOrderSelectionState>
  >(() => getInitialSelectionStateByID(selectedCareOrders, mode, nettedCareOrder));

  const onSelectionChanged = useCallbackRef((orderID: string, selected: boolean) => {
    setCareOrderSelectionStateByID(previousSelection => {
      const nextSelection = new Map(previousSelection);
      const previousSelectionState = nextSelection.get(orderID);
      nextSelection.set(orderID, {
        selected,
        hasBeenSelected: true,
        inNettedOrder: previousSelectionState?.inNettedOrder ?? false,
      });

      return nextSelection;
    });
  });

  const { data: careOrdersByID } = useGetCareOrdersQuery({});
  const allCareOrders = useMemo<SelectableCareOrder[]>(() => {
    if (!careOrdersByID) {
      return EMPTY_ARRAY;
    }

    return [...careOrdersByID.values()].map(
      careOrder => new SelectableCareOrder(careOrder, careOrderSelectionStateByID.get(careOrder.OrderID))
    );
  }, [careOrdersByID, careOrderSelectionStateByID]);

  const selectedCareOrderIDs = useMemo(
    () =>
      [...careOrderSelectionStateByID.entries()]
        .filter(([, selectionState]) => selectionState.selected)
        .map(([orderID]) => orderID),
    [careOrderSelectionStateByID]
  );

  return {
    allCareOrders,
    careOrdersByID,
    selectedCareOrderIDs,
    careOrderSelectionStateByID,
    onSelectionChanged,
  };
}
